import UserService from '@/services/UserService';

/**
 * Initial state
 */
const initialState = {
  profile: null,
  devices: [],
  sessions: [],
  loading: false,
  error: null
};

/**
 * User store module
 * 
 * Manages user profile data, devices, and sessions
 */
export const user = {
  namespaced: true,
  
  state: { ...initialState },
  
  mutations: {
    /**
     * Set loading state
     * 
     * @param {Object} state - Current state
     * @param {boolean} isLoading - New loading state
     */
    SET_LOADING(state, isLoading) {
      state.loading = isLoading;
    },
    
    /**
     * Set user profile data
     * 
     * @param {Object} state - Current state
     * @param {Object} profile - User profile data
     */
    SET_PROFILE(state, profile) {
      state.profile = profile;
      state.error = null;
    },
    
    /**
     * Set user devices list
     * 
     * @param {Object} state - Current state
     * @param {Array} devices - List of user devices
     */
    SET_DEVICES(state, devices) {
      state.devices = devices;
    },
    
    /**
     * Set user sessions list
     * 
     * @param {Object} state - Current state
     * @param {Array} sessions - List of user sessions
     */
    SET_SESSIONS(state, sessions) {
      state.sessions = sessions;
    },
    
    /**
     * Set error state
     * 
     * @param {Object} state - Current state
     * @param {Error} error - Error object
     */
    SET_ERROR(state, error) {
      state.error = error;
    },
    
    /**
     * Reset state to initial values
     * 
     * @param {Object} state - Current state
     */
    RESET(state) {
      Object.assign(state, { ...initialState });
    }
  },
  
  actions: {
    /**
     * Fetch user profile data
     * 
     * @param {Object} context - Vuex context
     */
    async fetchProfile({ commit }) {
      console.log('🔄 [user/fetchProfile] Starting profile fetch...');
      console.trace('📍 [user/fetchProfile] Call stack for fetchProfile');
      commit('SET_LOADING', true);
      
      try {
        const response = await UserService.getProfile();
        console.log('✅ [user/fetchProfile] Profile fetch successful');
        commit('SET_PROFILE', response.data);
      } catch (error) {
        console.error('❌ [user/fetchProfile] Error fetching user profile:', error);
        commit('SET_ERROR', error);
      } finally {
        commit('SET_LOADING', false);
      }
    },
    
    /**
     * Update user profile data
     * 
     * @param {Object} context - Vuex context
     * @param {Object} profileData - New profile data
     */
    async updateProfile({ commit }, profileData) {
      commit('SET_LOADING', true);
      
      try {
        const response = await UserService.updateProfile(profileData);
        commit('SET_PROFILE', response.data);
        return response.data;
      } catch (error) {
        commit('SET_ERROR', error);
        console.error('Error updating user profile:', error);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },
    
    /**
     * Fetch user devices
     * 
     * @param {Object} context - Vuex context
     */
    async fetchDevices({ commit }) {
      commit('SET_LOADING', true);
      
      try {
        const response = await UserService.getDevices();
        commit('SET_DEVICES', response.data);
      } catch (error) {
        commit('SET_ERROR', error);
        console.error('Error fetching user devices:', error);
      } finally {
        commit('SET_LOADING', false);
      }
    },
    
    /**
     * Update device trust status
     * 
     * @param {Object} context - Vuex context
     * @param {Object} params - Parameters
     * @param {string} params.deviceId - Device ID
     * @param {boolean} params.trusted - Trust status
     */
    async updateDeviceTrust({ commit, dispatch }, { deviceId, trusted }) {
      try {
        await UserService.updateDeviceTrust(deviceId, trusted);
        dispatch('fetchDevices');
      } catch (error) {
        commit('SET_ERROR', error);
        console.error('Error updating device trust status:', error);
        throw error;
      }
    },
    
    /**
     * Fetch user sessions
     * 
     * @param {Object} context - Vuex context
     */
    async fetchSessions({ commit }) {
      commit('SET_LOADING', true);
      
      try {
        const response = await UserService.getSessions();
        commit('SET_SESSIONS', response.data);
      } catch (error) {
        commit('SET_ERROR', error);
        console.error('Error fetching user sessions:', error);
      } finally {
        commit('SET_LOADING', false);
      }
    },
    
    /**
     * Terminate a specific session
     * 
     * @param {Object} context - Vuex context
     * @param {string} sessionId - Session ID to terminate
     */
    async terminateSession({ commit, dispatch }, sessionId) {
      try {
        await UserService.terminateSession(sessionId);
        dispatch('fetchSessions');
      } catch (error) {
        commit('SET_ERROR', error);
        console.error('Error terminating session:', error);
        throw error;
      }
    },
    
    /**
     * Terminate all sessions except current
     * 
     * @param {Object} context - Vuex context
     */
    async terminateAllSessions({ commit, dispatch }) {
      try {
        await UserService.terminateAllSessions();
        dispatch('fetchSessions');
      } catch (error) {
        commit('SET_ERROR', error);
        console.error('Error terminating all sessions:', error);
        throw error;
      }
    },
    
    /**
     * Reset user state
     * 
     * @param {Object} context - Vuex context
     */
    resetUserState({ commit }) {
      commit('RESET');
    }
  },
  
  getters: {
    /**
     * Get user profile data
     * 
     * @param {Object} state - Current state
     * @returns {Object} User profile
     */
    profile: state => state.profile,
    
    /**
     * Get user's full name
     * 
     * @param {Object} state - Current state
     * @returns {string} Full name
     */
    fullName: state => {
      if (!state.profile) return '';
      return `${state.profile.firstName} ${state.profile.lastName}`.trim();
    },
    
    /**
     * Get user's email
     * 
     * @param {Object} state - Current state
     * @returns {string} Email
     */
    email: state => state.profile?.email || '',
    
    /**
     * Get user's devices
     * 
     * @param {Object} state - Current state
     * @returns {Array} Devices
     */
    devices: state => state.devices,
    
    /**
     * Get user's active sessions
     * 
     * @param {Object} state - Current state
     * @returns {Array} Active sessions
     */
    activeSessions: state => state.sessions.filter(session => session.isActive),
    
    /**
     * Get loading state
     * 
     * @param {Object} state - Current state
     * @returns {boolean} Loading state
     */
    isLoading: state => state.loading,
    
    /**
     * Get error state
     * 
     * @param {Object} state - Current state
     * @returns {Error} Error object
     */
    error: state => state.error
  }
};

export default user;